import { useRef } from 'react'
import { Loader2, Upload } from 'lucide-react'

import { PdfPreview } from '@modules/component/PdfPreview/PdfPreview'
import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@modules/shadcn/ui/Dialog/Dialog'

import { useSimulationImageUpload } from '@ai-color-modules/simulation/hooks/useSimulationImageUpload'

interface SimulationImageUploaderProps {
  s3Upload: (file: File) => Promise<void>
  uploading: boolean
  inputId?: string
  ariaLabel?: string
  className?: string
  dropLabel?: string
  helperText?: string
  buttonLabel?: string
  dialogTitle?: string
  dialogDescription?: string
  dialogConfirmLabel?: string
  dialogCancelLabel?: string
  /** ダイアログ内（プレビュー下）に差し込む追加要素。ホスト側で顧客選択 UI 等を注入する（API 非依存を維持）。 */
  extraDialogContent?: React.ReactNode
  /**
   * ダイアログ内、プレビューより**上**に差し込む要素。アップロード前に必ず読ませたい注意書き用。
   * プレビューが背の高い PDF (h-[70vh]) の場合、下 (extraDialogContent) に置くと 13 インチ級の
   * 画面では fold 下に隠れて初期表示で 1 行も読めなくなるため、上に置く枠を分けている
   * (Figma node 13998-32061 でも注意書きは本文、プレビューは無し)。
   */
  dialogNoticeContent?: React.ReactNode
  /**
   * `confirmDisabled` が true のときに確定ボタンの近くへ出す理由。
   * フッターは固定領域なので、本文をスクロールしていても必ず読める
   * (PdM FB 2026-10-08 #4: 押せない理由が表示されなかった)。
   */
  confirmDisabledReason?: string
  /**
   * DialogFooter の左端（キャンセル / 確定ボタンの左）に差し込む要素。
   * 「しばらく表示しない」等、スクロールで流れると困る操作をフッター固定領域に置くための枠
   * (Figma node 13998-32061 の Footer 構成に対応)。
   */
  dialogFooterContent?: React.ReactNode
  /** input の accept 属性。既定 'image/*' (AI カラー既定)。PDF 等を扱うホストは上書きする。 */
  accept?: string
  /**
   * ダイアログ内プレビューの種類。既定 'image' (選択ファイルを <img> で表示)。
   * PDF を扱うホストは 'pdf' でブラウザネイティブの <object> プレビューに切り替える
   * (依存追加せず、選択直後に確認できる AI カラーと同等の体験を維持する)。
   * 'pdf' でも画像のファイルを選んだときは <img> で出す。
   */
  previewMode?: 'image' | 'pdf'
  /** 画像以外 (PDF 等) を扱うホスト向けに HEIC変換・圧縮をスキップする。既定 false (AI カラーの挙動を変えない) */
  skipCompression?: boolean
  /**
   * 確定ボタンを押させない条件をホスト側から足す。既定 false。
   * `extraDialogContent` に必須入力を差し込むホスト (企業画面の顧客欄) が使う。
   * ファイル未選択・変換中・エラーによる無効化は従来どおりこのコンポーネントが判断する。
   */
  confirmDisabled?: boolean
  /**
   * アップロードが失敗したとき、エラー文の下に添える一文（企業画面の「クレジットは消費されません」）。
   * 施主画面と共有する部品なので文言は持たず、ホストから受け取る。未指定なら何も出さない。
   * 通信の途中で切れるなど、サーバーの処理結果が分からない失敗では出さない（サーバーが成功して
   * いれば一文が事実に反する。`isOutcomeUnknownError`）。
   */
  uploadErrorNote?: string
}

export const SimulationImageUploader: React.FC<SimulationImageUploaderProps> = ({
  s3Upload,
  uploading,
  inputId = 'simulation-image-upload-input',
  ariaLabel = '現況画像をアップロード',
  className,
  dropLabel = 'ファイルをここにドラッグ&ドロップ',
  helperText = 'または',
  buttonLabel = 'ファイルを選択',
  dialogTitle = '現況画像をアップロード',
  dialogDescription = '選択した画像のプレビューを確認できます',
  dialogConfirmLabel = 'アップロード',
  dialogCancelLabel = 'キャンセル',
  extraDialogContent,
  dialogNoticeContent,
  confirmDisabledReason,
  dialogFooterContent,
  accept = 'image/*',
  previewMode = 'image',
  skipCompression = false,
  confirmDisabled = false,
  uploadErrorNote,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const {
    handleImageUpload,
    imagePreviews,
    isDialogOpen,
    uploadFile,
    error,
    uploadOutcomeUnknown,
    isProcessing,
    handleOpenChange,
    handleSelectClick,
    handleDropZoneKeyDown,
    handleProceed,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    dragActive,
  } = useSimulationImageUpload({
    fileInputRef,
    s3Upload,
    uploading,
    skipCompression,
  })

  // PDF プレビューかどうか。本文の高さ配分（プレビューが余りを吸う）を切り替える
  const isPdfPreview = previewMode === 'pdf'

  // 押せない理由とボタンを結ぶ id。inputId はホストごとに一意なのでそれを土台にする
  const confirmDisabledReasonId = `${inputId}-confirm-disabled-reason`

  const dropZoneClasses = cn(
    'relative flex w-full cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center transition-colors duration-200 hover:border-primary/60 hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/40',
    dragActive && 'border-primary bg-primary/10',
    className,
  )

  return (
    <>
      <input
        accept={accept}
        className="hidden"
        id={inputId}
        onChange={handleImageUpload}
        ref={fileInputRef}
        type="file"
      />
      <div
        aria-label={ariaLabel}
        className={dropZoneClasses}
        onClick={handleSelectClick}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onKeyDown={handleDropZoneKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="space-y-2">
          <label className="text-muted-foreground" htmlFor={inputId}>
            {dropLabel}
          </label>
          <p className="my-1 text-sm text-muted-foreground">{helperText}</p>
          <span className="inline-flex items-center rounded-md border border-slate-300 bg-white px-5 py-2 text-sm font-medium text-slate-600">
            <Upload aria-hidden className="mr-2 text-slate-400" size={16} />
            {buttonLabel}
          </span>
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        {/*
          flex 縦 + 本文だけスクロール + フッター固定。
          既定の DialogContent は grid かつ全体 overflow-auto のため、背の高いプレビュー
          (PDF の h-[70vh] 等) だとフッターの確認ボタンが fold 下に隠れ、小さい画面
          (13 インチ等) で「見えない」状態になっていた (issue #2489 レビュー指摘)。
          ヘッダー/フッターを固定し中央だけスクロールさせることで、プレビューを大きく
          保ったまま確認ボタンを常に表示する。画像プレビュー (AI カラー) は背が低く
          スクロールが出ないため見た目は不変。
        */}
        <DialogContent
          className={cn(
            'flex flex-col overflow-hidden max-w-xl md:max-w-2xl',
            // PDF は高さを固定する。`max-h` だけだと高さが内容依存（auto）になり、
            // 余りを吸わせるはずのプレビュー（flex-1）が伸びずに潰れる
            isPdfPreview ? 'h-[90vh]' : 'max-h-[90vh]',
          )}
        >
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            {dialogDescription ? <DialogDescription>{dialogDescription}</DialogDescription> : null}
          </DialogHeader>
          {/* PDF のときは本文を縦の flex にし、プレビューに「余った高さ」を吸わせる
              （`isPdfPreview` の分岐）。固定の高さ（h-[70vh] 等）だと、プレビューの下に置いた
              顧客欄が画面の高さによって fold 下に隠れる（PdM FB 2026-10-08 #4）。要素の並びは
              AI カラーに合わせたままにしたいので、並べ替えではなく高さの配分で解く
              （PdM FB 2026-10-12「AI カラーに合わせてほしい」）。
              画像（AI カラー）は従来どおり `space-y-4` の通常フローで、描画は変わらない */}
          <div
            className={cn(
              'min-h-0 flex-1 overflow-y-auto',
              isPdfPreview ? 'flex flex-col gap-4' : 'space-y-4',
            )}
          >
            {dialogNoticeContent}
            <div
              className={cn(
                'relative overflow-hidden rounded-xl bg-slate-100',
                // 余りを吸う側。下限（ここを割ると本文がスクロールに戻り、下の入力が
                // また fold 下へ回る）は、図面の取り違えに気づける最小として 140px
                isPdfPreview && 'flex min-h-[140px] flex-1',
              )}
            >
              {isProcessing ? (
                <div
                  aria-live="polite"
                  className="flex h-64 flex-col items-center justify-center gap-2 text-slate-400"
                  role="status"
                >
                  <Loader2 aria-hidden className="h-6 w-6 animate-spin" />
                  <p className="text-sm font-medium">画像を変換しています...</p>
                </div>
              ) : isPdfPreview &&
                imagePreviews &&
                !error &&
                // PDF のホストでも画像を受け付ける (積算は PNG / JPEG も解析できる) ので、
                // 画像を選んだときは <img> で出す
                !uploadFile?.type.startsWith('image/') ? (
                // 表示の作法（#view=Fit・フォールバック）は PdfPreview に集約している。
                // 外部帳票の取り込み（issue #2404）でも同じ見え方にするため共通化した
                <PdfPreview
                  ariaLabel={uploadFile ? `${uploadFile.name} のプレビュー` : 'PDFプレビュー'}
                  // 既定の h-[70vh] / max-h-[720px] は外し、親 (flex-1) の高さに従わせる
                  className="h-full max-h-none"
                  fallbackDetail={uploadFile?.name}
                  src={imagePreviews}
                />
              ) : imagePreviews && !error ? (
                <img
                  alt="アップロードされた現況写真"
                  className="h-auto w-full object-cover"
                  src={imagePreviews}
                />
              ) : (
                <div className="flex h-64 items-center justify-center text-slate-400">
                  {error ? '画像を読み込めませんでした' : '画像が選択されていません'}
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50 text-white">
                  <Loader2 aria-hidden className="h-6 w-6 animate-spin" />
                  <p className="text-sm font-medium">アップロードしています...</p>
                </div>
              )}
            </div>
            {error ? (
              <div role="alert">
                <p className="text-sm text-destructive">{error}</p>
                {uploadErrorNote && !uploadOutcomeUnknown ? (
                  <p className="text-xs text-muted-foreground">{uploadErrorNote}</p>
                ) : null}
              </div>
            ) : null}
            {extraDialogContent}
          </div>
          {/* 押せない理由は role="alert" にしない（開いた時点から出ている固定の案内で、
              操作に対する時間的な変化ではない）。確定ボタンから aria-describedby で指し、
              支援技術では「なぜ押せないか」をボタンの説明として読ませる */}
          {confirmDisabled && confirmDisabledReason ? (
            <p className="text-sm text-destructive" id={confirmDisabledReasonId}>
              {confirmDisabledReason}
            </p>
          ) : null}
          <DialogFooter className="gap-2 sm:justify-end">
            {dialogFooterContent ? (
              <div className="flex items-center sm:mr-auto">{dialogFooterContent}</div>
            ) : null}
            <Button onClick={() => handleOpenChange(false)} type="button" variant="ghost">
              {dialogCancelLabel}
            </Button>
            <Button
              aria-busy={isProcessing || uploading}
              aria-describedby={
                confirmDisabled && confirmDisabledReason ? confirmDisabledReasonId : undefined
              }
              disabled={uploading || isProcessing || !!error || !uploadFile || confirmDisabled}
              onClick={handleProceed}
              type="button"
            >
              {dialogConfirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
